import { BingMapCoordinates, FullLocation } from 'ts/types';
import { GRANTED } from 'constants/permissions';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getBrowserLocation } from 'redux/actions/browserLocationActions';
import { updateTemporalLocation } from 'redux/actions/locationActions';
import {
  selectBrowserLocationCoordinates,
  selectGeolocationStatusSelector,
} from 'redux/selectors/browserLocation';
import {
  currentLocationFormattedAddressSelector,
  currentLocationSelector,
} from 'redux/selectors/caseCreation';
import {
  currentLanguageSelector,
  mapDefaultLocationSelector,
} from 'redux/selectors/config';
import { temporalLocationSelector } from 'redux/selectors/location';
import type { CaseCreationDataValue } from 'ts/types/case-creation.types';
import type { PartiallyRequired } from 'ts/types/util.types';
import useAutocomplete from './useAutocomplete';
import type { UseLocationParams } from './types';

// We want to be present the key 'coordinates' for the pins
export type Pins = PartiallyRequired<any, 'coordinates'>;

const initializePins = (currentLocation: CaseCreationDataValue) =>
  currentLocation ? [currentLocation as FullLocation] : [];

const useLocation = (params?: UseLocationParams) => {
  const dispatch = useDispatch();
  const autocomplete = useAutocomplete(params);

  const browserCoordinates = useSelector(selectBrowserLocationCoordinates);
  const currentLanguage = useSelector(currentLanguageSelector);
  const currentLocationFullAddress = useSelector(currentLocationSelector);
  const geolocationStatus = useSelector(selectGeolocationStatusSelector);
  const mapDefaultLocation = useSelector(mapDefaultLocationSelector);
  const temporalLocation = useSelector(temporalLocationSelector);

  const selectedLocationFormattedAddress = useSelector(
    currentLocationFormattedAddressSelector,
  );
  const [isDefaultLocation, setIsDefaultLocation] = useState(true);
  const [pins, setPins] = useState<Pins[]>(() =>
    initializePins(currentLocationFullAddress),
  );
  const [center, setCenter] = useState<BingMapCoordinates | undefined>(
    (currentLocationFullAddress as FullLocation)?.coordinates,
  );
  const [zoom, setZoom] = useState(16);
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');

  // Effects
  useEffect(() => {
    if (params?.avoidInitialization) {
      return;
    }

    dispatch(getBrowserLocation());
  }, [params?.avoidInitialization, dispatch]);

  useEffect(() => {
    if (params?.avoidInitialization) {
      return;
    }
    autocomplete.setInputValue(
      (currentLocationFullAddress as FullLocation)?.address?.formattedAddress ||
        temporalLocation?.address?.formattedAddress ||
        '',
    );
    // This should only happen on the first render.
    // If not, we won't be able to clean the input
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Each render: Update the input to the selected one
  useEffect(() => {
    const address = temporalLocation?.address?.formattedAddress;
    if (address) {
      autocomplete.setInputValue(address);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocationFullAddress, temporalLocation?.address?.formattedAddress]);

  // SetPins effect
  useEffect(() => {
    if (params?.avoidInitialization) {
      setPins([]);
      return;
    }

    if (temporalLocation) {
      setPins([temporalLocation]);
      return;
    }

    // If we are on the default location and the autocomplete input is with information
    if (isDefaultLocation && autocomplete.inputValue) {
      setPins([mapDefaultLocation]);
      return;
    }

    setPins([]);
  }, [
    autocomplete.inputValue,
    isDefaultLocation,
    mapDefaultLocation,
    params?.avoidInitialization,
    temporalLocation,
  ]);

  // Methods
  const getAutocompleteOptionLabel = useCallback((option: any) => {
    if (typeof option === 'string') {
      return option;
    }
    if (!option) {
      return '';
    }
    return (option as FullLocation).address.formattedAddress;
  }, []);

  const handleSearch = (query: string) => {
    autocomplete.setOptions([]);
    setDebouncedSearchValue(query);
  };

  const handleOptionSelected = (location: FullLocation | undefined) => {
    autocomplete.handleOptionSelected();
    dispatch(updateTemporalLocation(location));
  };

  const clearField = () => {
    autocomplete.clearField();
    dispatch(updateTemporalLocation(undefined));
  };

  const handleGeolocation = (avoidGPS?: boolean) => {
    if (geolocationStatus === GRANTED && !avoidGPS) {
      setCenter(browserCoordinates);
      setIsDefaultLocation(false);
    } else {
      setCenter(mapDefaultLocation.coordinates as BingMapCoordinates);
      setIsDefaultLocation(true);
    }
  };

  return {
    autocomplete,
    browserCoordinates,
    center,
    clearField,
    currentLanguage,
    currentLocationFullAddress,
    debouncedSearchValue,
    getAutocompleteOptionLabel,
    handleGeolocation,
    handleOptionSelected,
    handleSearch,
    isDefaultLocation,
    pins,
    selectedLocationFormattedAddress,
    setCenter,
    setDebouncedSearchValue,
    setIsDefaultLocation,
    setPins,
    setZoom,
    temporalLocation,
    zoom,
  };
};

export { useLocation };
